import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  Icon,
  InputTextArea,
  InputDate,
} from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency, getContactNumber, pluralize } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement, useState, useEffect } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useCreateShipmentContext } from "../../../hooks/useCreateShipmentContext.shared";
import { Alert } from "../../alert/Alert";
import { ReactHookFormSwitch } from "../../form/ReactHookFormSwitch";
import { InputLabel, InputContainer } from "@sellar-io/components";

import { BasicDialog } from "../../modal/BasicDialog";
import { ProfilePicture } from "../../profilePicture/ProfilePicture";
import { OrderAddBoxModal } from "./OrderAddBoxDialog";
import { OrderCreateConsignmentBoxItem } from "./OrderCreateConsignmentBoxItem";
import { OrderCreateConsignmentUpdateSupplierDetailsModal } from "./OrderCreateConsignmentUpdateSupplierDetailsModal";
import { OrderUpdateEmailAddressModal } from "./OrderUpdateEmailAddressModal";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";

type OrderCreateConsignmentFormFieldValues = {
  isResidentialAddress: boolean;
  isSaturdayDelivery: boolean;
  deliveryInstructions: string | null;
  collectionDate: string;
  collectionInstructions: string | null;
  eoriNumber?: string;
};

const APC_WEEKEND_COLLECTION_ERROR_MESSAGE =
  "Weekend collection is not available when using APC";
const INSTRUCTIONS_MAX_LENGTH = 64;

export const OrderCreateConsignmentInformation = ({
  order,
  error,
  isSubmitting,
  onSubmit,
}: {
  order: OrderGetResponseType;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: SubmitHandler<OrderCreateConsignmentFormFieldValues>;
}) => {
  const { boxes, setBoxes, items } = useCreateShipmentContext();
  const allocatedItems = boxes.map((box) => box.OrderItemIds).flat().length;
  const [isUnboxed, setIsUnboxed] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [emailDialogActive, setEmailDialogActive] = useState<boolean>(false);
  const [supplierUpdateActive, setSupplierUpdateActive] =
    useState<boolean>(false);
  const [
    unallocatedItemsWarningDialogActive,
    setUnallocatedItemsWarningDialogActive,
  ] = useState(false);

  // Reset boxes when fulfillment type is UPS or order changes
  useEffect(() => {
    if (order.fulfillmentType === "ups") {
      setBoxes([]);
    }
  }, [order.id, order.fulfillmentType, setBoxes]);

  const form = useForm<OrderCreateConsignmentFormFieldValues>({
    shouldUseNativeValidation: true,
    defaultValues: {
      isResidentialAddress: false,
      isSaturdayDelivery: false,
      deliveryInstructions:
        order.deliveryNotes?.slice(0, INSTRUCTIONS_MAX_LENGTH) ?? null,
      collectionDate: order.dispatchPlannedAt
        ? dayjs(order.dispatchPlannedAt).format("YYYY-MM-DD")
        : dayjs().add(1, "day").format("YYYY-MM-DD"),
      collectionInstructions: null,
      eoriNumber: undefined,
    },
  });

  const supplierRelationshipQuery = useSupplierRelationshipList(
    {
      query: [
        {
          type: "buyerId",
          modifier: "EQUALS",
          value: order.RetailerId,
        },
      ],
      limit: 1,
    },

    {
      enabled: !!order.RetailerId,
    },
  );

  const retailerContactSms = getContactNumber(
    order,
    supplierRelationshipQuery.data?.pages[0]?.[0]?.buyerContactSmsNumber,
  );

  const collectionAddressEnabled = useRemoteFeatureFlags().isActive(
    "collectionAddressCreation",
  );

  const collectionAddress =
    collectionAddressEnabled && order.Supplier.Business.CollectionAddress
      ? order.Supplier.Business.CollectionAddress
      : order.Supplier.Business.DeliveryAddress;

  const isApcFulfillmentType = order.fulfillmentType === "apc";

  const instructionsMaxLength = INSTRUCTIONS_MAX_LENGTH;
  const deliveryInstructions = useWatch({
    control: form.control,
    name: "deliveryInstructions",
  });
  const collectionInstructions = useWatch({
    control: form.control,
    name: "collectionInstructions",
  });

  // commenting out for now
  // const requiredEoic = collectionAddress.postalCode.slice(0, 2) === "BT";

  const submitForm = async () => {
    await form.handleSubmit(async (data) => {
      if ((data.deliveryInstructions?.length ?? 0) > instructionsMaxLength) {
        form.setError("deliveryInstructions", {
          message: `Maximum ${instructionsMaxLength} characters`,
        });
        form.setFocus("deliveryInstructions");
        return;
      }

      if ((data.collectionInstructions?.length ?? 0) > instructionsMaxLength) {
        form.setError("collectionInstructions", {
          message: `Maximum ${instructionsMaxLength} characters`,
        });
        form.setFocus("collectionInstructions");
        return;
      }

      // Validate that a weekend has not been selected for APC fulfillment
      if (isApcFulfillmentType) {
        const isWeekend = getIsWeekend(dayjs(data.collectionDate).toDate());
        if (isWeekend) {
          form.setError("collectionDate", {
            message: APC_WEEKEND_COLLECTION_ERROR_MESSAGE,
          });
          form.setFocus("collectionDate");
          return;
        }
      }

      await onSubmit({
        ...data,
        deliveryInstructions:
          data.deliveryInstructions?.slice(0, instructionsMaxLength) ?? null,
        collectionInstructions:
          data.collectionInstructions?.slice(0, instructionsMaxLength) ?? null,
      });
    })();
  };

  return (
    <div className={"m-auto flex max-w-[900px] flex-col gap-4 p-4"}>
      <form className={"flex flex-col gap-4"}>
        <Card className="bg-white">
          <CardContent className={"grid grid-cols-2 gap-y-4 p-4"}>
            <div className="flex flex-row items-center gap-2">
              <ProfilePicture
                logoUrl={order.Retailer.Business.logoUrl}
                name={order.Retailer.Business.name}
                size={"small"}
              />

              <div className="flex flex-col">
                <H5>
                  <span>{order.Retailer.Business.name}</span> -{" "}
                  {formatCurrency(order.price, order.currency)}
                </H5>
                <div className="text-sm text-gray-500">Order #{order.id}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader className="flex flex-col bg-white p-4">
            <H5>Shipment labels</H5>
            <H6
              className={`flex items-center ${
                allocatedItems == items.length
                  ? "text-green-500"
                  : "text-orange-500"
              }`}
            >
              {allocatedItems} of {items.length} order{" "}
              {pluralize("item", items.length)} allocated
              {boxes && boxes.length > 0
                ? ` (${boxes.length} ${pluralize("label", boxes.length)})`
                : ""}
              {allocatedItems == items.length && (
                <Icon className="ml-1" icon="CHECK_CIRCLE" />
              )}
            </H6>
          </CardHeader>
          <CardContent className="p-4">
            {boxes.map((box, i) => (
              <OrderCreateConsignmentBoxItem
                key={box.OrderItemIds.join("")}
                index={i}
                box={box}
                items={items}
                onRemove={() => {
                  if (i > -1) {
                    const array = boxes;
                    array.splice(i, 1);
                    const newBoxes = [...array];
                    setBoxes(newBoxes);
                  }
                }}
              />
            ))}

            <div className="grid gap-2">
              {order.fulfillmentType === "ups" && (
                <Alert
                  type="information"
                  title={
                    "All items MUST be boxed to avoid additional handling charges from UPS. This includes plastic kegs."
                  }
                  message=""
                  className="w-full"
                />
              )}
              <Button
                disabled={items.length === allocatedItems}
                className={twMerge(
                  "w-full",
                  order.fulfillmentType === "ups" ? "col-span-1" : "col-span-2",
                )}
                color={"secondary"}
                onClick={() => {
                  setIsUnboxed(false);
                  setActive(true);
                }}
              >
                <Icon icon={"PLUS_CIRCLE"} />
                Item
              </Button>
            </div>
          </CardContent>
        </Card>
        {isApcFulfillmentType && (
          <Card className="bg-white">
            <CardContent className={"p-4"}>
              <InputLabel className={"mt-2"}>Collection Date</InputLabel>
              <div className="flex flex-col gap-1">
                <InputContainer>
                  <InputDate
                    className={"w-full"}
                    {...form.register("collectionDate", {
                      required: isApcFulfillmentType,
                      valueAsDate: true,
                      validate: (value) => {
                        const date = dayjs(value).toDate();
                        const isWeekend = getIsWeekend(date);
                        if (isWeekend) {
                          return APC_WEEKEND_COLLECTION_ERROR_MESSAGE;
                        }
                        return true;
                      },
                    })}
                    disabled={isSubmitting}
                    min={dayjs().add(1, "day").format("YYYY-MM-DD")}
                  />
                </InputContainer>
                {form.formState.errors.collectionDate && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.collectionDate.message}
                  </p>
                )}
              </div>
              <div className="mt-2 mb-2">
                <div className="flex flex-col rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <div className="flex items-center gap-1.5 pl-1">
                    <div>
                      <Icon icon="INFORMATION" />
                    </div>
                    <p className="font-semibold">
                      Important Collection Details
                    </p>
                  </div>
                  <ul className="flex list-disc flex-col pl-7 text-sm">
                    <li>
                      Next day collection available when requesting before 6pm.
                    </li>
                    <li>
                      APC will collect any time between 8am and 5pm on the
                      collection date you specify here.
                    </li>
                    <li>
                      Same day collection is not available when using APC.
                    </li>
                    <li>Weekend collection is not available when using APC.</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <InstructionsHeading
                  title="Collection instructions"
                  maxLength={instructionsMaxLength}
                  currentLength={collectionInstructions?.length ?? 0}
                />
                <InputContainer>
                  <InputTextArea
                    variant="legacyFixed"
                    {...form.register("collectionInstructions", {
                      required: false,
                      maxLength: instructionsMaxLength,
                    })}
                    disabled={isSubmitting}
                    maxLength={instructionsMaxLength}
                    placeholder={
                      "Provide any instructions to help the driver successfully collect this order from you"
                    }
                    className="placeholder-zinc-400 placeholder:font-light"
                  />
                </InputContainer>
              </div>
              {/*// commenting out for now*/}
              {/*{requiredEoic ? (*/}
              {/*  <div className="flex flex-col gap-2">*/}
              {/*    <InputLabel>EORI number</InputLabel>*/}
              {/*    <InputContainer>*/}
              {/*      <InputText*/}
              {/*        {...form.register("eoriNumber", {*/}
              {/*          required: false,*/}
              {/*        })}*/}
              {/*      />*/}
              {/*    </InputContainer>*/}
              {/*    <InputNote>*/}
              {/*      APC require an EORI number when shipping goods from Northern*/}
              {/*      Ireland to Great Britain mainland.*/}
              {/*    </InputNote>*/}
              {/*  </div>*/}
              {/*) : null}*/}
            </CardContent>
          </Card>
        )}
        <Card className="bg-white">
          <CardHeader className="bg-white p-4">
            <H5>Delivery Info</H5>
          </CardHeader>
          <CardContent className={"flex flex-col gap-6 p-4"}>
            <div className="grid md:grid-cols-2">
              <div className="col-span-1">
                <UpsOrderAddress
                  address={collectionAddress}
                  businessName={order.Supplier.Business.name}
                  title={"Ship from"}
                />
              </div>
              <div className="col-span-1">
                <UpsOrderAddress
                  address={order.DeliveryAddress}
                  businessName={order.Retailer.Business.name}
                  title={"Ship to"}
                />
              </div>
            </div>
            <div className={"flex gap-2"}>
              {order.fulfillmentType === "ups" ? (
                <>
                  <div className="flex w-full flex-wrap">
                    <div className="flex w-full">
                      <ReactHookFormSwitch
                        control={form.control}
                        name={`isResidentialAddress`}
                      />
                      <H5 className="ml-2">Is this a residential address?</H5>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex w-full flex-col gap-4">
                    {/*<div className="flex gap-2">*/}
                    {/*  <ReactHookFormSwitch*/}
                    {/*    control={form.control}*/}
                    {/*    name={`isSaturdayDelivery`}*/}
                    {/*  />*/}
                    {/*  <H5>Saturday delivery</H5>*/}
                    {/*</div>*/}
                    <div className="flex flex-col gap-2">
                      <InstructionsHeading
                        title="Delivery instructions"
                        maxLength={instructionsMaxLength}
                        currentLength={deliveryInstructions?.length ?? 0}
                      />
                      <InputContainer>
                        <InputTextArea
                          variant="legacyFixed"
                          {...form.register("deliveryInstructions", {
                            required: false,
                            maxLength: instructionsMaxLength,
                          })}
                          disabled={isSubmitting}
                          maxLength={instructionsMaxLength}
                          placeholder={
                            "Provide any instructions to help the driver successfully deliver this order to your customer."
                          }
                          className="placeholder-zinc-400 placeholder:font-light"
                        />
                      </InputContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader
            className={twMerge(
              "bg-white",
              "flex items-center justify-between p-4",
            )}
          >
            <div className="flex flex-col">
              <H5>Your Contact Info</H5>
            </div>
            <Button
              onClick={() => setSupplierUpdateActive(true)}
              color={"secondary"}
            >
              <Icon icon={"PENCIL"} />
              Edit
            </Button>
          </CardHeader>
          <CardContent className={"p-4"}>
            <div className="grid md:grid-cols-2">
              <div className="col-span-1 flex flex-col">
                <div className="flex gap-2">
                  <H5>Email Address</H5>
                </div>
                <H6>{order.Supplier.Business.email}</H6>
              </div>
              {order.Supplier.Business.phoneNumber && (
                <div className="col-span-1 flex flex-col">
                  <H5>Phone number</H5>
                  <H6>{order.Supplier.Business.phoneNumber}</H6>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader
            className={twMerge(
              "bg-white",
              "flex items-center justify-between p-4",
            )}
          >
            <div className="flex flex-col">
              <H5>Customer Contact Info</H5>
            </div>
            {!!supplierRelationshipQuery.data?.pages[0]?.[0] && (
              <Button
                onClick={() => setEmailDialogActive(true)}
                color={"secondary"}
              >
                <Icon icon={"PENCIL"} />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className={"p-4"}>
            <div className="grid md:grid-cols-2">
              <div className="col-span-1 flex flex-col">
                <div className="flex gap-2">
                  <H5>Email Address</H5>
                </div>
                <H6>
                  {supplierRelationshipQuery.data?.pages[0]?.[0]
                    ?.buyerContactEmail ?? order.Retailer.Business.email}
                </H6>
              </div>
              {retailerContactSms && (
                <div className="col-span-1 flex flex-col">
                  <H5>Phone number</H5>
                  <H6> {retailerContactSms}</H6>
                </div>
              )}
            </div>
          </CardContent>
          <CardContent className={"border-t border-solid border-gray-200 p-4"}>
            <p>
              By selecting 'Create Shipment' you agree to the{" "}
              <Anchor
                href="https://sellar.io/terms"
                className="inline"
                target="_blank"
              >
                Terms of Service
              </Anchor>{" "}
              and associated charges
            </p>
          </CardContent>
        </Card>

        {error && <Alert type={"error"} message={error} className={"w-full"} />}

        <div className="flex w-full justify-end">
          <Button
            type="button"
            disabled={!allocatedItems || isSubmitting}
            onClick={() => {
              // warn user if attempting to create consignment without all items
              if (allocatedItems != items.length) {
                return setUnallocatedItemsWarningDialogActive(true);
              }

              // all items allocated, submit
              submitForm();
            }}
            className={"w-full md:w-auto"}
          >
            Create shipment
          </Button>
          <UnallocatedItemsWarningDialog
            active={unallocatedItemsWarningDialogActive}
            onDismiss={() => setUnallocatedItemsWarningDialogActive(false)}
            onConfirm={() => {
              // on acknowledge warning, submit
              setUnallocatedItemsWarningDialogActive(false);
              submitForm();
            }}
            allocatedItemCount={allocatedItems}
            totalItemCount={items.length}
          />
        </div>
      </form>
      {!!supplierRelationshipQuery.data?.pages[0]?.[0] && (
        <OrderUpdateEmailAddressModal
          onDismiss={() => setEmailDialogActive(false)}
          active={emailDialogActive}
          order={order}
          connectionId={supplierRelationshipQuery.data.pages[0][0].id}
        />
      )}

      <OrderCreateConsignmentUpdateSupplierDetailsModal
        onDismiss={() => setSupplierUpdateActive(false)}
        active={supplierUpdateActive}
        email={order.Supplier.Business.email}
        phoneNumber={order.Supplier.Business.phoneNumber}
        businessId={order.Supplier.BusinessId}
      />

      <OrderAddBoxModal
        fulfillmentType={order.fulfillmentType}
        active={active}
        isUnboxed={isUnboxed}
        onDismiss={() => setActive(false)}
        dispatchPlannedAt={order.dispatchPlannedAt ?? null}
        onConfirm={({ selectedLabelOrderItemIds, weight }) => {
          const newBoxes = [
            ...(boxes ? boxes : []),
            {
              OrderItemIds: selectedLabelOrderItemIds,
              weight: weight,
              isUnboxed,
            },
          ];
          setBoxes(newBoxes);
        }}
      />
    </div>
  );
};

const UnallocatedItemsWarningDialog = ({
  allocatedItemCount,
  totalItemCount,
  active,
  onDismiss,
  onConfirm,
}: {
  allocatedItemCount: number;
  totalItemCount: number;
  active: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
}) => {
  return (
    <BasicDialog
      onDismiss={onDismiss}
      onConfirm={onConfirm}
      confirmButtonText={"Proceed"}
      dialogActive={active}
      title={"Create shipment"}
      description={"Are you sure you want to proceed?"}
    >
      <Alert
        type={"warning"}
        title={`Only ${allocatedItemCount} of ${totalItemCount} order items have been allocated a shipping label`}
        message={
          "By proceeding, you acknowledge that you are responsible for organising the remainder of this shipment outside of Sellar."
        }
      />
    </BasicDialog>
  );
};

type UpsOrderAddressProps = {
  address: {
    name: string;
    street: string;
    city: string;
    region: string | null;
    postalCode: string;
    country: string;
  };
  businessName: string;
  title: string;
  toggleName?: boolean;
};

const UpsOrderAddress = (props: UpsOrderAddressProps): ReactElement => {
  const address = `${props.address.name} ${props.address.street}`;
  return (
    <div className="flex justify-between">
      <div className="flex flex-col gap-2">
        {props.title ? <H5>{props.title}</H5> : null}
        <div className={"select-text"}>
          {props.toggleName && <H6>{props.businessName}</H6>}
          <H6>{address.slice(0, 35)}</H6>
          {!props.toggleName && address.length > 35 && (
            <H6>{address.slice(35, 70)}</H6>
          )}
          <H6>{props.address.city}</H6>
          <H6>{props.address.region}</H6>
          <H6>
            {props.address.postalCode}, {props.address.country}
          </H6>
        </div>
      </div>
    </div>
  );
};

const InstructionsHeading = ({
  title,
  currentLength,
  maxLength,
}: {
  title: string;
  currentLength: number;
  maxLength: number;
}) => {
  const remaining = maxLength - currentLength;
  const isNearLimit = remaining <= 5;

  return (
    <div className="flex items-center justify-between">
      <InputLabel>{title}</InputLabel>
      <span
        className={`text-sm ${
          isNearLimit ? "font-medium text-orange-500" : "text-zinc-500"
        }`}
      >
        {currentLength}/{maxLength}
      </span>
    </div>
  );
};

const getIsWeekend = (date: Date) => {
  const day = dayjs(date).day();

  const isWeekend = day === 0 || day === 6; // 0 = Sunday 6 = Saturday

  return isWeekend;
};
